import type { Gradient, GradientStop } from './types.js';
import { darken, rotateHue } from '../color/manipulate.js';
import { withAlpha } from '../color/opacity.js';

function renderStops(stops: GradientStop[]): string {
  return stops
    .map((s) => (s.position !== undefined ? `${s.color} ${s.position}%` : s.color))
    .join(', ');
}

export function gradientToCss(g: Gradient): string {
  const stops = renderStops(g.stops);
  switch (g.kind) {
    case 'linear':
      return `linear-gradient(${g.angle ?? 135}deg, ${stops})`;
    case 'radial':
      return `radial-gradient(${g.shape ?? 'circle'}, ${stops})`;
    case 'conic':
      return `conic-gradient(from ${g.angle ?? 0}deg, ${stops})`;
  }
}

export function gradientToCssVar(g: Gradient, name: string): string {
  return `--${name}: ${gradientToCss(g)};`;
}

export function reverseGradient(g: Gradient): Gradient {
  return { ...g, stops: [...g.stops].reverse() };
}

export function recolorGradient(g: Gradient, hueShift: number): Gradient {
  return { ...g, stops: g.stops.map((s) => ({ ...s, color: rotateHue(s.color, hueShift) })) };
}

export function darkenGradient(g: Gradient, amount = 0.15): Gradient {
  return { ...g, stops: g.stops.map((s) => ({ ...s, color: darken(s.color, amount) })) };
}

export function glassifyGradient(g: Gradient, alpha = 0.6): Gradient {
  return { ...g, stops: g.stops.map((s) => ({ ...s, color: withAlpha(s.color, alpha) })) };
}

export function dominantStop(g: Gradient): string {
  // For luminance/contrast calculations: the middle stop carries the most
  // weight visually for a balanced gradient. For 2-stop gradients we average.
  if (g.stops.length === 1) return g.stops[0]?.color ?? '#000000';
  const middle = Math.floor(g.stops.length / 2);
  return g.stops[middle]?.color ?? g.stops[0]?.color ?? '#000000';
}
